import { createContext } from "react";
import { ClientRepType } from "./types";
import { GetKickOffQuery, KickOffTemplate, Project, ProjectKickOffs } from "@/graphql/generated/graphql";
import { AddressType } from "@/types/globals";
import { Activity, ManagersType, NoteSectionType, Settings } from "@/components/KickOffs/types";

export interface KickOffContextValueType {
  // Draft
  kickOffId: string | null;
  kickoffData: GetKickOffQuery['getKickOff'] | undefined;
  loadingContext: boolean;

  // Project
  projectData: Partial<Project>;
  setProjectData: React.Dispatch<React.SetStateAction<Partial<Project>>>;

  // Client — either an existing client (selectedClientId) or a new one (clientName + clientCompanyNumber)
  selectedClientId: string | null;
  setSelectedClientId: React.Dispatch<React.SetStateAction<string | null>>;
  clientName: string | null;
  setClientName: React.Dispatch<React.SetStateAction<string | null>>;
  clientCompanyNumber: string | null;
  setClientCompanyNumber: React.Dispatch<React.SetStateAction<string | null>>;

  // Client reps — can be existing or new; each has an optional title (stored in ProjectUser)
  clientReps: ClientRepType[];
  setClientReps: React.Dispatch<React.SetStateAction<ClientRepType[]>>;

  // Title — resolved title (user input or generated fallback)
  kickOffTitle: string;
  setKickOffTitle: React.Dispatch<React.SetStateAction<string>>;

  // Basics
  addresses: AddressType[];
  setAddresses: React.Dispatch<React.SetStateAction<AddressType[]>>;

  // Actions & Notes
  noteSections: NoteSectionType[];
  setNoteSections: React.Dispatch<React.SetStateAction<NoteSectionType[]>>;

  // Project Details
  managers: ManagersType[];
  setManagers: React.Dispatch<React.SetStateAction<ManagersType[]>>;
  activities: Activity[];
  setActivities: React.Dispatch<React.SetStateAction<Activity[]>>;
  contractTypeIds: string[];
  setContractTypeIds: React.Dispatch<React.SetStateAction<string[]>>;
  projectTagIds: string[];
  setProjectTagIds: React.Dispatch<React.SetStateAction<string[]>>;
  projectSettings: Settings[];
  setProjectSettings: React.Dispatch<React.SetStateAction<Settings[]>>;

  // Review & Send
  sharedWithEmails: string[];
  setSharedWithEmails: React.Dispatch<React.SetStateAction<string[]>>;
  generatedPDF: Blob | null;
  setGeneratedPDF: React.Dispatch<React.SetStateAction<Blob | null>>;

  // Actions
  updateNotes: (sections: NoteSectionType[]) => void;
  SaveAndNext: (currentStep: number) => void;

  // Preload Draft KickOffs
  selectedPreloadKickOff: Pick<ProjectKickOffs, 'id' | 'title'> | null;
  onPreloadKickOffChange: (kickOff: Pick<ProjectKickOffs, 'id' | 'title'> | null) => void;

  // New Template
  newTemplate: boolean;
  setNewTemplate: React.Dispatch<React.SetStateAction<boolean>>;
  newTemplateTitle: string;
  setNewTemplateTitle: React.Dispatch<React.SetStateAction<string>>;

  // Preload Template
  selectedTemplate: Pick<KickOffTemplate, 'id' | 'title'> | null;
  onPreloadTemplateChange: (template: Pick<KickOffTemplate, "id" | "title"> | null) => void;

  // Existing Project (for "Create KickOff from Project" f`low)
  existingProjectId: string | null | undefined;
}

export const KickOffContext = createContext<KickOffContextValueType>({
  kickOffId: null,
  kickoffData: undefined,
  loadingContext: true,
  projectData: {},
  setProjectData: () => { },
  selectedClientId: null,
  setSelectedClientId: () => { },
  clientName: null,
  setClientName: () => { },
  clientCompanyNumber: null,
  setClientCompanyNumber: () => { },
  clientReps: [],
  setClientReps: () => { },
  kickOffTitle: '',
  setKickOffTitle: () => { },
  addresses: [],
  setAddresses: () => { },
  noteSections: [],
  setNoteSections: () => { },
  managers: [],
  setManagers: () => { },
  activities: [],
  setActivities: () => { },
  contractTypeIds: [],
  setContractTypeIds: () => { },
  projectTagIds: [],
  setProjectTagIds: () => { },
  projectSettings: [],
  setProjectSettings: () => { },
  sharedWithEmails: [],
  setSharedWithEmails: () => { },
  generatedPDF: null,
  setGeneratedPDF: () => { },
  updateNotes: () => { },
  SaveAndNext: () => { },
  selectedPreloadKickOff: null,
  onPreloadKickOffChange: () => { },
  newTemplate: false,
  setNewTemplate: () => { },
  newTemplateTitle: "",
  setNewTemplateTitle: () => { },
  selectedTemplate: null,
  onPreloadTemplateChange: () => { },
  existingProjectId: null,
});
